import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Trophy } from "lucide-react";
import { Card, CardHeader, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";

const EMPTY = {
  name: "",
  season: "",
  organizer: "",
  description: "",
};

export default function ChampionshipForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const isEditing = !!id;

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [error, setError] = useState("");

  const canManage = !!user?.capabilities?.manage_events;

  useEffect(() => {
    if (!isEditing) return;
    api
      .get(`/api/championships/${id}/`)
      .then((res) => setForm({ ...EMPTY, ...res.data }))
      .catch(() => setError(t("common.loadError")))
      .finally(() => setFetching(false));
  }, [id, isEditing, t]);

  if (user && !canManage) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const payload = {
      name: form.name,
      season: form.season,
      organizer: form.organizer,
      description: form.description,
    };

    try {
      if (isEditing) {
        await api.put(`/api/championships/${id}/`, payload);
        navigate(`/app/championships/${id}`);
      } else {
        const res = await api.post("/api/championships/", payload);
        navigate(`/app/championships/${res.data.id}`);
      }
    } catch (err) {
      const detail = err.response?.data;
      setError(
        typeof detail === "object"
          ? Object.values(detail).flat().join(" ")
          : t("common.saveError")
      );
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="w-full animate-fade-in">
      <button
        onClick={() => navigate("/app/championships")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("common.back")}
      </button>

      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Trophy className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">
                {isEditing ? t("championship.edit") : t("championship.new")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {isEditing
                  ? t("championship.editSubtitle")
                  : t("championship.newSubtitle")}
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="w-full space-y-6">
            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label htmlFor="name">{t("championship.name")}</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={set("name")}
                  required
                  maxLength={200}
                  placeholder={t("championship.namePlaceholder")}
                />
              </div>

              <div>
                <Label htmlFor="season">{t("championship.season")}</Label>
                <Input
                  id="season"
                  value={form.season || ""}
                  onChange={set("season")}
                  maxLength={40}
                  placeholder={t("championship.seasonPlaceholder")}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("championship.seasonHint")}
                </p>
              </div>

              <div>
                <Label htmlFor="organizer">{t("championship.organizer")}</Label>
                <Input
                  id="organizer"
                  value={form.organizer || ""}
                  onChange={set("organizer")}
                  maxLength={200}
                  placeholder={t("championship.organizerPlaceholder")}
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="description">{t("championship.description")}</Label>
                <textarea
                  id="description"
                  value={form.description || ""}
                  onChange={set("description")}
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-border pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/app/championships")}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={loading}>
                {isEditing ? t("common.save") : t("common.create")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
